// import external libraries
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import components
import { Autocomplete, TextField } from '@mui/material';

// import utils
import {
  renderFilterOption,
  renderFilterTags
} from '../../BacklogFilters.utils';

// import types
import type { AutocompleteRenderInputParams } from '@mui/material';
import type { BacklogFilterMultiSelectProps } from './BacklogFilterMultiSelect.types';

/**
 * The one control every filter field in the popover is drawn with. Everything presentational lives here:
 * a caller supplies its options and how to label them, never an Autocomplete of its own.
 *
 * The one behavioural distinction it makes is *who filters*. With `onQueryChange` the caller is fetching
 * for the typed term and this keeps every option it is handed; without it the whole list is already in
 * memory and MUI filters it locally.
 */
const BacklogFilterMultiSelect = <Option,>({
  label,
  options,
  selected,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  required,
  disabled,
  onQueryChange
}: BacklogFilterMultiSelectProps<Option>) => {
  'use memo';

  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');

  const callerOwnsTheQuery = !!onQueryChange;

  return (
    <Autocomplete<Option, true>
      multiple
      // Several values per field is the normal case: the list stays open while ticking.
      disableCloseOnSelect
      disabled={disabled}
      value={selected}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderFilterOption(getOptionLabel)}
      // One pick is named, several are summed into a count, so the field stays one line.
      renderTags={renderFilterTags(getOptionLabel, t('selected').toLowerCase())}
      filterOptions={callerOwnsTheQuery ? (returned) => returned : undefined}
      noOptionsText={null}
      inputValue={query}
      onInputChange={(_event, value, reason) => {
        const next = reason === 'reset' ? '' : value;
        setQuery(next);
        onQueryChange?.(next);
      }}
      onChange={(_event, value) => onChange([...value])}
      fullWidth
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          required={required}
          error={required && selected.length === 0}
        />
      )}
    />
  );
};

export default BacklogFilterMultiSelect;
